import { createTheme, alpha } from '@mui/material/styles';
import {
  brandColors,
  defaultSecondaryColor,
  mutedSuccessColor,
  mutedWarningColor,
  mutedErrorColor,
} from './ThemeColors';

const defaultTheme = createTheme();
const sharedBorderRadius = 4;
export const formRadius = sharedBorderRadius;
const surfaceShadow = '0 12px 30px rgba(15, 23, 42, 0.08)';
const softShadow = '0 8px 24px rgba(15, 23, 42, 0.06)';

// export const getCurrentBrand = () => {
//   if (typeof window !== 'undefined') {
//     const selectedTheme = localStorage.getItem('selectedTheme');
//     if (!selectedTheme) {
//       localStorage.setItem('selectedTheme', 'default');
//       return brandColors.default;
//     }
//     return brandColors[selectedTheme] || brandColors.default;
//   }
//   return brandColors.default;
// };

export const getCurrentBrand = () => brandColors.default;

const _brand = getCurrentBrand();
export const brand = new Proxy(_brand, {
  get(_, prop) {
    return getCurrentBrand()[prop];
  },
});

export const gray = {
  50: 'hsl(220, 35%, 97%)',
  100: 'hsl(220, 30%, 94%)',
  200: 'hsl(220, 20%, 88%)',
  300: 'hsl(220, 20%, 80%)',
  400: 'hsl(220, 20%, 65%)',
  500: 'hsl(220, 20%, 42%)',
  600: 'hsl(220, 20%, 35%)',
  700: 'hsl(220, 20%, 25%)',
  800: 'hsl(220, 30%, 6%)',
  900: 'hsl(220, 35%, 3%)',
};

export const getDesignTokens = (mode = 'light') => {
  const currentBrand = getCurrentBrand();
  const darkBrand = {
    light: currentBrand[300],
    main: currentBrand[400],
    dark: currentBrand[700],
  };

  const isDark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        light: isDark ? darkBrand.light : currentBrand[200],
        main: isDark ? darkBrand.main : currentBrand[600],
        dark: isDark ? darkBrand.dark : currentBrand[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: defaultSecondaryColor[200],
        main: defaultSecondaryColor[500],
        dark: defaultSecondaryColor[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: currentBrand[100],
        main: currentBrand[300],
        dark: currentBrand[600],
        contrastText: isDark ? gray[900] : gray[50],
      },
      warning: {
        light: mutedWarningColor[200],
        main: mutedWarningColor[500],
        dark: mutedWarningColor[700],
      },
      error: {
        light: mutedErrorColor[200],
        main: mutedErrorColor[500],
        dark: mutedErrorColor[700],
      },
      success: {
        light: mutedSuccessColor[200],
        main: mutedSuccessColor[500],
        dark: mutedSuccessColor[700],
      },
      grey: { ...gray },
      divider: isDark ? alpha(gray[700], 0.6) : alpha(gray[300], 0.8),
      background: isDark
        ? {
          default: '#0B1220',
          paper: '#111827',
        }
        : {
          default: '#F4F6FB',
          paper: '#FFFFFF',
        },
      text: {
        primary: isDark ? '#F9FAFB' : '#111827',
        secondary: isDark ? '#9CA3AF' : '#64748B',
        warning: mutedWarningColor[500],
      },
      action: {
        hover: isDark ? alpha(gray[700], 0.28) : alpha(gray[200], 0.35),
        selected: isDark ? alpha(gray[600], 0.35) : alpha(currentBrand[100], 0.65),
      },
    },
    typography: {
      fontFamily: 'Inter, "Segoe UI", Roboto, sans-serif',
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: {
        fontSize: defaultTheme.typography.pxToRem(52),
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(40),
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(32),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(15),
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(15),
        lineHeight: 1.6,
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
        lineHeight: 1.6,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 500,
        lineHeight: 1.5,
      },
      overline: {
        fontSize: defaultTheme.typography.pxToRem(11),
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: sharedBorderRadius,
    },
    shadows: [
      'none',
      '0px 1px 2px rgba(15, 23, 42, 0.04)',
      '0px 2px 6px rgba(15, 23, 42, 0.05)',
      '0px 4px 10px rgba(15, 23, 42, 0.06)',
      '0px 6px 14px rgba(15, 23, 42, 0.07)',
      '0px 8px 18px rgba(15, 23, 42, 0.08)',
      '0px 10px 24px rgba(15, 23, 42, 0.08)',
      '0px 12px 28px rgba(15, 23, 42, 0.09)',
      '0px 14px 32px rgba(15, 23, 42, 0.09)',
      '0px 16px 36px rgba(15, 23, 42, 0.10)',
      '0px 18px 40px rgba(15, 23, 42, 0.10)',
      '0px 20px 44px rgba(15, 23, 42, 0.11)',
      '0px 22px 48px rgba(15, 23, 42, 0.11)',
      '0px 24px 52px rgba(15, 23, 42, 0.12)',
      '0px 26px 56px rgba(15, 23, 42, 0.12)',
      '0px 28px 60px rgba(15, 23, 42, 0.13)',
      '0px 30px 64px rgba(15, 23, 42, 0.13)',
      '0px 32px 68px rgba(15, 23, 42, 0.14)',
      '0px 34px 72px rgba(15, 23, 42, 0.14)',
      '0px 36px 76px rgba(15, 23, 42, 0.15)',
    ],
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: sharedBorderRadius,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            padding: '10px 16px',
          },
          contained: {
            boxShadow: 'none',
          },
          outlined: {
            borderWidth: 1.5,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: sharedBorderRadius,
            boxShadow: surfaceShadow,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: sharedBorderRadius,
            backgroundImage: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: softShadow,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        },
      },
    },
  };
};
